// =============================================================================
// AI IMAGE AFTER - Génération d'images "APRÈS" via IA pour ANAH
// Version améliorée avec références visuelles et régénération
// =============================================================================

import { useState, useRef } from "react";
import {
  Wand2,
  Upload,
  Download,
  RefreshCw,
  ImagePlus,
  Sparkles,
  AlertCircle,
  CheckCircle2,
  X,
  Maximize2,
  Columns,
  Image as ImageIcon,
  Plus,
  Trash2,
  RotateCcw,
  Eye
} from "lucide-react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { enteteTenant } from "../../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL;

// Styles de génération
const GENERATION_STYLES = [
  { 
    value: "realistic", 
    label: "Réaliste (photo)", 
    description: "Rendu photo-réaliste de la pièce rénovée",
    prompt_suffix: "photorealistic, high quality photograph, professional interior photography, natural lighting"
  },
  { 
    value: "architectural", 
    label: "Schématique (architectural)", 
    description: "Style technique pour dossier ANAH",
    prompt_suffix: "architectural rendering, technical drawing style, clean lines, professional blueprint aesthetic"
  }
];

// Éléments de salle de bain PMR avec descriptions détaillées pour l'IA
const SDB_ELEMENTS = [
  { 
    id: "douche_italienne", 
    label: "Douche à l'italienne PMR", 
    selected: true,
    description: "walk-in shower with zero threshold entry, large shower head, glass panel",
    category: "douche"
  },
  { 
    id: "bac_douche", 
    label: "Receveur de douche extra-plat", 
    selected: false,
    description: "extra-flat shower tray with anti-slip surface",
    category: "douche"
  },
  { 
    id: "barres_appui_douche", 
    label: "Barres d'appui douche", 
    selected: true,
    description: "chrome grab bars mounted on shower walls, L-shaped and straight bars",
    category: "securite"
  },
  { 
    id: "barres_appui_wc", 
    label: "Barres d'appui WC", 
    selected: true,
    description: "grab bars next to toilet, fold-down support bar",
    category: "securite"
  },
  { 
    id: "siege_douche", 
    label: "Siège de douche rabattable", 
    selected: true,
    description: "wall-mounted fold-down shower seat in white or chrome",
    category: "douche"
  },
  { 
    id: "mitigeur_thermo", 
    label: "Mitigeur thermostatique", 
    selected: true,
    description: "thermostatic mixer tap with temperature control and anti-scald protection",
    category: "robinetterie"
  },
  { 
    id: "douchette_main", 
    label: "Douchette à main sur barre", 
    selected: true,
    description: "handheld shower head on adjustable slide bar",
    category: "douche"
  },
  { 
    id: "lavabo_pmr", 
    label: "Lavabo PMR suspendu", 
    selected: false,
    description: "wall-mounted accessible sink with knee clearance underneath",
    category: "lavabo"
  },
  { 
    id: "miroir_inclinable", 
    label: "Miroir inclinable", 
    selected: false,
    description: "tilting mirror for wheelchair users",
    category: "accessoire"
  },
  { 
    id: "wc_rehausse", 
    label: "WC surélevé", 
    selected: false,
    description: "raised height toilet (comfort height) for easier transfer",
    category: "wc"
  },
  { 
    id: "sol_antiderapant", 
    label: "Sol antidérapant", 
    selected: true,
    description: "anti-slip floor tiles in neutral color (beige, gray, or white)",
    category: "sol"
  },
  { 
    id: "carrelage_mural", 
    label: "Carrelage mural clair", 
    selected: true,
    description: "light colored wall tiles (white, cream, or light gray), large format tiles",
    category: "mur"
  },
  { 
    id: "eclairage_led", 
    label: "Éclairage LED intégré", 
    selected: false,
    description: "LED ceiling lights and mirror lighting, bright and even illumination",
    category: "eclairage"
  },
  { 
    id: "niche_rangement", 
    label: "Niche de rangement", 
    selected: false,
    description: "built-in wall niche for shower products",
    category: "accessoire"
  },
  { 
    id: "paroi_verre", 
    label: "Paroi de douche en verre", 
    selected: true,
    description: "clear glass shower screen or panel, frameless design",
    category: "douche"
  }
];

// Références visuelles de SDB PMR (URLs d'images de référence)
const REFERENCE_STYLES = [
  {
    id: "moderne_blanc",
    name: "Moderne blanc",
    description: "Carrelage blanc, douche italienne, style épuré",
    colors: "white tiles, chrome fixtures, minimalist design"
  },
  {
    id: "contemporain_gris",
    name: "Contemporain gris",
    description: "Tons gris, béton ciré, moderne",
    colors: "gray tiles, concrete look, contemporary style"
  },
  {
    id: "chaleureux_bois",
    name: "Chaleureux bois",
    description: "Touches de bois, ambiance spa",
    colors: "wood accents, warm tones, spa-like atmosphere"
  },
  {
    id: "classique_beige",
    name: "Classique beige",
    description: "Beige et crème, intemporel",
    colors: "beige and cream tiles, classic timeless design"
  }
];

export default function AIImageAfter({ 
  dossierId,
  tenantId,
  beforeImageUrl,
  preconisations = [],
  roomDimensions = { width: 246, height: 160 },
  onImageGenerated 
}) {
  const [loading, setLoading] = useState(false);
  const [loadingBoth, setLoadingBoth] = useState(false);
  const [error, setError] = useState(null);
  const [beforeImage, setBeforeImage] = useState(beforeImageUrl || null);
  
  // Images générées pour chaque style
  const [realisticImage, setRealisticImage] = useState(null);
  const [architecturalImage, setArchitecturalImage] = useState(null);
  
  // Historique des générations pour comparaison
  const [generationHistory, setGenerationHistory] = useState([]);
  
  const [selectedStyle, setSelectedStyle] = useState("realistic");
  const [selectedReferenceStyle, setSelectedReferenceStyle] = useState("moderne_blanc");
  
  // Éléments sélectionnés (travaux/équipements)
  const [elements, setElements] = useState(() => {
    if (preconisations.length > 0) {
      return SDB_ELEMENTS.map(el => ({
        ...el,
        selected: preconisations.some(p => 
          p.toLowerCase().includes(el.label.toLowerCase()) || 
          el.label.toLowerCase().includes(p.toLowerCase())
        ) || el.selected
      }));
    }
    return SDB_ELEMENTS;
  });
  
  const [customPrompt, setCustomPrompt] = useState("");
  const [viewFullscreen, setViewFullscreen] = useState(null);
  
  // Dimensions de la pièce
  const [dimensions, setDimensions] = useState({
    width: roomDimensions.width || 246,
    height: roomDimensions.height || 160
  });
  
  // Compteur de générations
  const [generationCount, setGenerationCount] = useState(0);
  
  const fileInputRef = useRef(null);

  // Upload de l'image "AVANT"
  const handleBeforeImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Veuillez sélectionner une image");
      return;
    }

    const localUrl = URL.createObjectURL(file);
    setBeforeImage({ file, localUrl });
    setError(null);
  };

  // Toggle un élément
  const toggleElement = (elementId) => {
    setElements(elements.map(el => 
      el.id === elementId ? { ...el, selected: !el.selected } : el
    ));
  };

  // Sélectionner/désélectionner par catégorie
  const toggleCategory = (category, value) => {
    setElements(elements.map(el => 
      el.category === category ? { ...el, selected: value } : el
    ));
  };

  // Construire le prompt détaillé
  const buildDetailedPrompt = (style) => {
    const selectedElements = elements.filter(el => el.selected);
    const styleConfig = GENERATION_STYLES.find(s => s.value === style);
    const referenceStyle = REFERENCE_STYLES.find(s => s.id === selectedReferenceStyle);
    
    // Construire la liste des éléments avec leurs descriptions
    const elementsDescription = selectedElements
      .map(el => el.description)
      .join(", ");
    
    // Prompt structuré et détaillé
    let prompt = `Generate a professional interior photograph of a fully renovated accessible bathroom (PMR - Persons with Reduced Mobility) for French ANAH documentation.

ROOM SPECIFICATIONS:
- Dimensions: ${dimensions.width}cm wide x ${dimensions.height}cm deep (${(dimensions.width/100).toFixed(1)}m x ${(dimensions.height/100).toFixed(1)}m)
- Surface area: ${((dimensions.width * dimensions.height) / 10000).toFixed(2)} square meters

MANDATORY ACCESSIBILITY FEATURES TO SHOW:
${selectedElements.map(el => `- ${el.label}: ${el.description}`).join('\n')}

STYLE AND ATMOSPHERE:
- Référence style: ${referenceStyle?.name || "Modern white"}
- Colors and materials: ${referenceStyle?.colors || "white tiles, chrome fixtures"}
${customPrompt ? `- Additional requirements: ${customPrompt}` : ""}

IMPORTANT REQUIREMENTS:
- The bathroom must look realistic and professionally photographed
- All accessibility features must be clearly visible
- The space must appear functional and compliant with PMR standards
- Show proper spacing for wheelchair access where applicable
- Professional interior photography lighting

OUTPUT STYLE: ${styleConfig?.prompt_suffix || "photorealistic"}`;
    
    return prompt;
  };

  // Générer une image
  const generateImage = async (style) => {
    const token = localStorage.getItem("session_token");
    const formData = new FormData();
    
    const hasBeforeImage = beforeImage && (beforeImage.file || typeof beforeImage === "string");
    if (hasBeforeImage) {
      if (beforeImage.file) {
        formData.append("before_image", beforeImage.file);
      } else if (typeof beforeImage === "string") {
        formData.append("before_image_url", beforeImage);
      }
    } else {
      formData.append("generate_only", "true");
    }
    
    formData.append("prompt", buildDetailedPrompt(style));
    formData.append("style", style);
    formData.append("renovations", JSON.stringify(elements.filter(el => el.selected).map(el => el.label)));
    
    if (dossierId) {
      formData.append("dossier_id", dossierId);
    }

    const response = await fetch(`${API}/api/dal/generate-after-image`, {
      method: "POST",
      headers: {
        ...enteteTenant(tenantId),
        ...(token && { Authorization: `Bearer ${token}` })
      },
      body: formData
    });

    if (!response.ok) {
      const errData = await response.json();
      throw new Error(errData.detail || "Erreur lors de la génération");
    }

    const data = await response.json();
    
    if (data.image_base64) {
      return `data:image/png;base64,${data.image_base64}`;
    } else if (data.image_url) {
      return data.image_url;
    }
    
    throw new Error("Aucune image générée");
  };

  // Générer une seule image
  const generateSingleImage = async () => {
    setLoading(true);
    setError(null);

    try {
      const imageUrl = await generateImage(selectedStyle);
      
      if (selectedStyle === "realistic") {
        setRealisticImage(imageUrl);
      } else {
        setArchitecturalImage(imageUrl);
      }
      
      // Ajouter à l'historique
      setGenerationHistory(prev => [...prev, {
        id: Date.now(),
        style: selectedStyle,
        imageUrl,
        elements: elements.filter(el => el.selected).map(el => el.label),
        timestamp: new Date().toISOString()
      }]);
      
      setGenerationCount(prev => prev + 1);

      if (onImageGenerated) {
        onImageGenerated({ image_url: imageUrl, style: selectedStyle });
      }
    } catch (err) {
      console.error("Erreur génération:", err);
      setError(err.message || "Erreur lors de la génération de l'image");
    } finally {
      setLoading(false);
    }
  };

  // Générer les deux versions
  const generateBothStyles = async () => {
    setLoadingBoth(true);
    setError(null);

    try {
      const [realistic, architectural] = await Promise.all([
        generateImage("realistic"),
        generateImage("architectural")
      ]);
      
      setRealisticImage(realistic);
      setArchitecturalImage(architectural);
      
      setGenerationCount(prev => prev + 2);

      if (onImageGenerated) {
        onImageGenerated({ 
          realistic_url: realistic, 
          architectural_url: architectural,
          both: true 
        });
      }
    } catch (err) {
      console.error("Erreur génération:", err);
      setError(err.message || "Erreur lors de la génération des images");
    } finally {
      setLoadingBoth(false);
    }
  };

  // Régénérer avec les nouveaux éléments
  const regenerate = async () => {
    if (realisticImage || architecturalImage) {
      await generateSingleImage();
    }
  };

  // Télécharger une image
  const downloadImage = (imageUrl, style) => {
    if (!imageUrl) return;
    
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = `SDB_PMR_APRES_${style}_${new Date().toISOString().slice(0, 10)}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Réinitialiser la sélection
  const resetSelection = () => {
    setElements(SDB_ELEMENTS);
    setCustomPrompt("");
    setSelectedReferenceStyle("moderne_blanc");
  };

  const isLoading = loading || loadingBoth;
  const hasGeneratedImages = realisticImage || architecturalImage;
  
  // Grouper les éléments par catégorie
  const categories = [...new Set(elements.map(el => el.category))];
  const categoryLabels = {
    douche: "Douche",
    securite: "Sécurité",
    robinetterie: "Robinetterie",
    lavabo: "Lavabo",
    wc: "WC",
    sol: "Sol",
    mur: "Murs",
    eclairage: "Éclairage",
    accessoire: "Accessoires"
  };

  return (
    <div className="space-y-6" data-testid="ai-image-after">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Wand2 className="w-5 h-5 text-[var(--oh-text)]" />
          </div>
          <div>
            <h3 className="text-[var(--oh-text)] font-medium">Génération IA - Vue APRÈS</h3>
            <p className="text-[var(--oh-text-secondary)] text-sm">Salle de bain PMR pour dossier ANAH</p>
          </div>
        </div>
        {generationCount > 0 && (
          <Badge className="bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)]">
            {generationCount} génération{generationCount > 1 ? "s" : ""}
          </Badge>
        )}
      </div>

      {/* Dimensions */}
      <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-4 space-y-3">
        <Label className="text-[var(--oh-text-secondary)] flex items-center gap-2">
          <Badge className="bg-[var(--oh-info-bg)] text-[var(--oh-info-text)]">Dimensions</Badge>
          Taille de la pièce
        </Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-[var(--oh-text-secondary)] text-xs">Largeur (cm)</Label>
            <Input
              type="number"
              value={dimensions.width}
              onChange={(e) => setDimensions({ ...dimensions, width: parseInt(e.target.value) || 0 })}
              className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
            />
          </div>
          <div>
            <Label className="text-[var(--oh-text-secondary)] text-xs">Longueur (cm)</Label>
            <Input
              type="number"
              value={dimensions.height}
              onChange={(e) => setDimensions({ ...dimensions, height: parseInt(e.target.value) || 0 })}
              className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
            />
          </div>
          <div>
            <Label className="text-[var(--oh-text-secondary)] text-xs">Surface</Label>
            <div className="h-10 flex items-center px-3 bg-[var(--oh-bg-elevated)] rounded-md text-[var(--oh-text)]">
              {((dimensions.width * dimensions.height) / 10000).toFixed(2)} m²
            </div>
          </div>
        </div>
      </div>

      {/* Style de référence */}
      <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-4 space-y-3">
        <Label className="text-[var(--oh-text-secondary)]">Style de référence</Label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {REFERENCE_STYLES.map(style => (
            <button
              key={style.id}
              onClick={() => setSelectedReferenceStyle(style.id)}
              className={`p-3 rounded-lg text-left transition-all ${
                selectedReferenceStyle === style.id
                  ? "bg-[var(--oh-accent-bg)] border-2 border-[var(--oh-accent)]"
                  : "bg-[var(--oh-bg-elevated)] border-2 border-transparent hover:border-[var(--oh-border)]"
              }`}
            >
              <p className={`font-medium text-sm ${selectedReferenceStyle === style.id ? "text-[var(--oh-text)]" : "text-[var(--oh-text-secondary)]"}`}>
                {style.name}
              </p>
              <p className="text-[var(--oh-text-secondary)] text-xs mt-1">{style.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Éléments à inclure */}
      <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-4 space-y-4">
        <div className="flex items-center justify-between">
          <Label className="text-[var(--oh-text-secondary)]">Équipements et travaux à visualiser</Label>
          <Button
            size="sm"
            variant="ghost"
            onClick={resetSelection}
            className="text-[var(--oh-text-secondary)] hover:text-[var(--oh-text)]"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Réinitialiser
          </Button>
        </div>
        
        {/* Par catégorie */}
        {categories.map(category => (
          <div key={category} className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-[var(--oh-text-secondary)] text-xs uppercase tracking-wide">
                {categoryLabels[category] || category}
              </Label>
              <div className="flex gap-1">
                <button
                  onClick={() => toggleCategory(category, true)}
                  className="text-xs text-[var(--oh-success-text)] hover:underline"
                >
                  Tout
                </button>
                <span className="text-[var(--oh-text-secondary)]">|</span>
                <button
                  onClick={() => toggleCategory(category, false)}
                  className="text-xs text-[var(--oh-error-text)] hover:underline"
                >
                  Rien
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {elements.filter(el => el.category === category).map(element => (
                <button
                  key={element.id}
                  onClick={() => toggleElement(element.id)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                    element.selected
                      ? "bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)] border border-[var(--oh-accent)]"
                      : "bg-[var(--oh-bg-elevated)] text-[var(--oh-text-secondary)] border border-[var(--oh-border)] hover:border-[var(--oh-border)]"
                  }`}
                >
                  {element.selected && <CheckCircle2 className="w-3 h-3 inline mr-1" />}
                  {element.label}
                </button>
              ))}
            </div>
          </div>
        ))}
        
        <p className="text-[var(--oh-text-secondary)] text-xs">
          {elements.filter(el => el.selected).length} élément{elements.filter(el => el.selected).length > 1 ? "s" : ""} sélectionné{elements.filter(el => el.selected).length > 1 ? "s" : ""}
        </p>
      </div>

      {/* Instructions personnalisées */}
      <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-4 space-y-2">
        <Label className="text-[var(--oh-text-secondary)]">Instructions supplémentaires</Label>
        <Textarea
          value={customPrompt}
          onChange={(e) => setCustomPrompt(e.target.value)}
          placeholder="Ex: Préférence pour un style épuré, couleur spécifique du carrelage, contraintes particulières..."
          className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] min-h-[80px]"
        />
      </div>

      {/* Photo AVANT (optionnel) */}
      <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-4 space-y-3">
        <Label className="text-[var(--oh-text-secondary)] flex items-center gap-2">
          <Badge className="bg-[var(--oh-error-bg)] text-[var(--oh-error-text)]">AVANT</Badge>
          Photo actuelle (optionnel - aide l'IA à comprendre l'espace)
        </Label>
        
        {beforeImage ? (
          <div className="relative group inline-block">
            <img
              src={beforeImage.localUrl || beforeImage}
              alt="Photo AVANT"
              className="h-24 object-cover rounded-lg border border-[var(--oh-border)]"
            />
            <button
              onClick={() => setBeforeImage(null)}
              className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full"
            >
              <X className="w-3 h-3 text-[var(--oh-text)]" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => fileInputRef.current?.click()}
            className="h-20 w-32 border-2 border-dashed border-[var(--oh-border)] rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[var(--oh-accent)] transition-colors"
          >
            <Upload className="w-5 h-5 text-[var(--oh-text-secondary)] mb-1" />
            <span className="text-[var(--oh-text-secondary)] text-xs">Ajouter</span>
          </button>
        )}
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleBeforeImageUpload}
        />
      </div>

      {/* Erreur */}
      {error && (
        <div className="bg-[var(--oh-error-bg)] border border-[var(--oh-error-border)] rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-[var(--oh-error-text)] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-[var(--oh-error-text)] font-medium">Erreur de génération</p>
            <p className="text-[var(--oh-error-text)] text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Boutons de génération */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <div className="flex items-center gap-2">
          <Select value={selectedStyle} onValueChange={setSelectedStyle}>
            <SelectTrigger className="w-full max-w-[180px] bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GENERATION_STYLES.map(style => (
                <SelectItem key={style.value} value={style.value}>
                  {style.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={generateSingleImage}
            disabled={isLoading}
            style={{ background: "transparent", border: "1px solid var(--oh-accent)", color: "var(--oh-accent-text)" }}
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <ImageIcon className="w-4 h-4 mr-2" />
            )}
            {hasGeneratedImages ? "Régénérer" : "Générer"}
          </Button>
        </div>

        <Button
          onClick={generateBothStyles}
          disabled={isLoading}
          className="px-6"
          style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" }}
        >
          {loadingBoth ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Génération...
            </>
          ) : (
            <>
              <Columns className="w-4 h-4 mr-2" />
              Comparer 2 styles
            </>
          )}
        </Button>
      </div>

      {/* Résultats */}
      {(realisticImage || architecturalImage || isLoading) && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-[var(--oh-text)] font-medium flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[var(--oh-accent-text)]" />
              Résultats générés - Vue APRÈS
            </h4>
            {hasGeneratedImages && (
              <p className="text-[var(--oh-text-secondary)] text-xs">
                Modifiez les éléments ci-dessus et cliquez "Régénérer" pour ajuster
              </p>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Style Réaliste */}
            <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-[var(--oh-text-secondary)] flex items-center gap-2">
                  <Badge className="bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)]">Réaliste</Badge>
                </Label>
                {realisticImage && (
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setViewFullscreen(realisticImage)}>
                      <Maximize2 className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => downloadImage(realisticImage, "realiste")}>
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
              
              {(loading && selectedStyle === "realistic") || loadingBoth ? (
                <div className="h-48 bg-[var(--oh-bg-elevated)] rounded-lg flex flex-col items-center justify-center">
                  <RefreshCw className="w-8 h-8 text-[var(--oh-accent-text)] animate-spin mb-3" />
                  <p className="text-[var(--oh-text-secondary)] text-sm">Génération en cours...</p>
                </div>
              ) : realisticImage ? (
                <img
                  src={realisticImage}
                  alt="Rendu réaliste"
                  className="w-full h-48 object-cover rounded-lg border-2 border-[var(--oh-accent)] cursor-pointer"
                  onClick={() => setViewFullscreen(realisticImage)}
                />
              ) : (
                <div className="h-48 bg-[var(--oh-bg-elevated)] rounded-lg flex items-center justify-center">
                  <p className="text-[var(--oh-text-secondary)] text-xs">Non généré</p>
                </div>
              )}
            </div>

            {/* Style Architectural */}
            <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-[var(--oh-text-secondary)] flex items-center gap-2">
                  <Badge className="bg-[var(--oh-success-bg)] text-[var(--oh-success-text)]">Schématique</Badge>
                </Label>
                {architecturalImage && (
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setViewFullscreen(architecturalImage)}>
                      <Maximize2 className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => downloadImage(architecturalImage, "schematique")}>
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
              
              {(loading && selectedStyle === "architectural") || loadingBoth ? (
                <div className="h-48 bg-[var(--oh-bg-elevated)] rounded-lg flex flex-col items-center justify-center">
                  <RefreshCw className="w-8 h-8 text-[var(--oh-success-text)] animate-spin mb-3" />
                  <p className="text-[var(--oh-text-secondary)] text-sm">Génération en cours...</p>
                </div>
              ) : architecturalImage ? (
                <img
                  src={architecturalImage}
                  alt="Rendu schématique"
                  className="w-full h-48 object-cover rounded-lg border-2 border-[var(--oh-success-border)] cursor-pointer"
                  onClick={() => setViewFullscreen(architecturalImage)}
                />
              ) : (
                <div className="h-48 bg-[var(--oh-bg-elevated)] rounded-lg flex items-center justify-center">
                  <p className="text-[var(--oh-text-secondary)] text-xs">Non généré</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Historique des générations */}
      {generationHistory.length > 1 && (
        <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-4 space-y-3">
          <Label className="text-[var(--oh-text-secondary)] flex items-center gap-2">
            <Eye className="w-4 h-4" />
            Historique ({generationHistory.length} versions)
          </Label>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {generationHistory.map((gen, index) => (
              <button
                key={gen.id}
                onClick={() => setViewFullscreen(gen.imageUrl)}
                className="flex-shrink-0"
              >
                <img
                  src={gen.imageUrl}
                  alt={`Version ${index + 1}`}
                  className="w-20 h-20 object-cover rounded-lg border border-[var(--oh-border)] hover:border-[var(--oh-accent)] transition-colors"
                />
                <p className="text-[var(--oh-text-secondary)] text-xs mt-1 text-center">V{index + 1}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Info */}
      <p className="text-center text-[var(--oh-text-secondary)] text-xs">
        Modifiez les éléments et régénérez autant de fois que nécessaire.
      </p>

      {/* Modal plein écran */}
      {viewFullscreen && (
        <div 
          className="fixed inset-0 z-50 bg-[var(--oh-bg)] flex items-center justify-center p-4"
          onClick={() => setViewFullscreen(null)}
        >
          <button
            className="absolute top-4 right-4 p-2 bg-[var(--oh-bg-surface)]/10 rounded-full hover:bg-[var(--oh-bg-surface)]/20"
            onClick={() => setViewFullscreen(null)}
          >
            <X className="w-6 h-6 text-[var(--oh-text)]" />
          </button>
          <img
            src={viewFullscreen}
            alt="Vue plein écran"
            className="max-w-full max-h-full object-contain rounded-lg"
          />
        </div>
      )}
    </div>
  );
}
